import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { syncPersonFromCcms, type SyncPersonFromCcmsResult } from "./syncPersonFromCcms";

export type LookupPersonInCcmsState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "not_in_ccms" }
  | { phase: "timeout" }
  | { phase: "error"; message: string };

export function useLookupPersonInCcms(pgaId: string, onFound: (pgaId: string) => void) {
  const apolloClient = useApolloClient();
  const { getAccessToken } = useCurrentUser();
  const [state, setState] = useState<LookupPersonInCcmsState>({ phase: "idle" });
  const abortRef = useRef<AbortController | null>(null);
  const onFoundRef = useRef(onFound);
  onFoundRef.current = onFound;

  useEffect(() => {
    setState({ phase: "idle" });
    abortRef.current?.abort();
    abortRef.current = null;
  }, [pgaId]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const lookup = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ phase: "loading" });

    const result: SyncPersonFromCcmsResult = await syncPersonFromCcms({
      pgaId,
      getAccessToken,
      apolloClient,
      signal: controller.signal
    });

    if (controller.signal.aborted) {
      return;
    }

    switch (result.status) {
      case "found":
        setState({ phase: "idle" });
        onFoundRef.current(result.pgaId);
        break;
      case "not_in_ccms":
        setState({ phase: "not_in_ccms" });
        break;
      case "timeout":
        setState({ phase: "timeout" });
        break;
      case "error":
        setState({ phase: "error", message: result.message });
        break;
    }
  }, [apolloClient, getAccessToken, pgaId]);

  return { state, lookup };
}
